import React, { useEffect, useMemo, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import {
  addDoc, collection, deleteDoc, doc, increment, onSnapshot, serverTimestamp, setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  BellOff, ChevronLeft, Clock, Cpu, Eraser, Plus, RotateCw, Save, SlidersHorizontal, Trash2, Activity, Wrench, X,
} from 'lucide-react';
import { analyticsDb, analyticsFunctions, db } from '../firebase';
import { Link } from 'react-router-dom';
import CustomDropdown from '../components/CustomDropdown';
import {
  DEFAULT_HOURS, DEFAULT_RULES, RULE_META, WEEKDAYS, mergeAutoFreeSpace, mergeMaintenance, mergeRules,
} from '../utils/analyticsRules';

// Settings -> Problem rules (MDM analytics T5). Edits Firestore mdm-analitics config/rules (thresholds,
// business hours per group, scan interval) and mutes/*. The analytics functions pick a saved template up
// within a minute; "Recalculate now" re-evaluates the whole fleet immediately.

const Toggle = ({ on, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    aria-pressed={on}
    className={`w-10 h-5 flex items-center rounded-full px-0.5 transition-colors duration-300 shrink-0 ${on ? 'bg-brand' : 'bg-white/[0.1]'}`}
  >
    <span className={`w-4 h-4 bg-white rounded-full transform transition-transform duration-300 ${on ? 'translate-x-5' : ''}`} />
  </button>
);

const SCAN_OPTIONS = [5, 15, 30, 60].map((m) => ({ label: `Every ${m} min`, value: String(m) }));

function RuleRow({ code, rule, onChange }) {
  const meta = RULE_META[code];
  return (
    <div className={`card-inset p-3 flex flex-col lg:flex-row lg:items-center gap-3 ${rule.enabled ? '' : 'opacity-60'}`}>
      <div className="flex items-start gap-3 lg:w-80 shrink-0">
        <Toggle on={!!rule.enabled} label={meta.label} onClick={() => onChange({ ...rule, enabled: !rule.enabled })} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">{meta.label}</p>
          <p className="text-xs text-gray-500">{meta.help}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 lg:ml-auto">
        {meta.fields.map(([key, label, unit]) => {
          const tone = key.startsWith('crit') ? 'text-red-300' : key.startsWith('warn') ? 'text-amber-300' : 'text-gray-400';
          return (
            <label key={key} className="flex flex-col gap-1">
              <span className={`text-[11px] uppercase tracking-wider ${tone}`}>{label}</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  step="any"
                  min="0"
                  disabled={!rule.enabled}
                  value={rule[key] ?? ''}
                  onChange={(e) => onChange({ ...rule, [key]: e.target.value === '' ? '' : Number(e.target.value) })}
                  className="input-field w-24 py-1.5"
                />
                <span className="text-xs text-gray-500 w-10">{unit}</span>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function HoursEditor({ value, onChange }) {
  const v = value || DEFAULT_HOURS;
  const days = v.days || [];
  // "24:00" is not a valid <input type="time"> value, so all-day is its own switch.
  const allDay = v.from === '00:00' && v.to === '24:00';
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <Toggle on={allDay} label="Open 24 hours" onClick={() => onChange({ ...v, ...(allDay ? { from: '08:00', to: '23:00' } : { from: '00:00', to: '24:00' }) })} />
        <span className="text-sm text-gray-300">24 h</span>
      </div>
      {!allDay && (
        <>
          <input type="time" value={v.from} onChange={(e) => onChange({ ...v, from: e.target.value })} className="input-field w-28 py-1.5" />
          <span className="text-gray-500 text-sm">to</span>
          <input type="time" value={v.to} onChange={(e) => onChange({ ...v, to: e.target.value })} className="input-field w-28 py-1.5" />
        </>
      )}
      <div className="flex gap-1">
        {WEEKDAYS.map(([n, label]) => {
          const on = days.includes(n);
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange({ ...v, days: on ? days.filter((d) => d !== n) : [...days, n].sort() })}
              className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors ${on ? 'brand-pill' : 'bg-white/[0.02] border-white/[0.08] text-gray-500 hover:text-gray-300'}`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AnalyticsRulesPage() {
  const [loaded, setLoaded] = useState(false);
  const [saved, setSaved] = useState(null);
  const [rules, setRules] = useState(mergeRules({}));
  const [defaultHours, setDefaultHours] = useState(DEFAULT_HOURS);
  const [groupHours, setGroupHours] = useState({});
  const [scanEvery, setScanEvery] = useState('15');
  const [autoFree, setAutoFree] = useState(mergeAutoFreeSpace());
  const [autoSummary, setAutoSummary] = useState(null);
  const [maint, setMaint] = useState(mergeMaintenance());
  const [excludeToAdd, setExcludeToAdd] = useState('');
  const [groups, setGroups] = useState({});
  const [mutes, setMutes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [recalc, setRecalc] = useState(false);
  const [muteForm, setMuteForm] = useState({ kind: 'device', key: '', until: '', reason: '' });
  const [groupToAdd, setGroupToAdd] = useState('');

  useEffect(() => onSnapshot(doc(analyticsDb, 'config', 'rules'), (snap) => {
    const d = snap.exists() ? snap.data() : {};
    setSaved(d);
    setRules(mergeRules(d.rules));
    setDefaultHours((d.businessHours && d.businessHours.default) || DEFAULT_HOURS);
    setGroupHours((d.businessHours && d.businessHours.groups) || {});
    setScanEvery(String(d.scanEveryMinutes || 15));
    setAutoFree(mergeAutoFreeSpace(d.autoFreeSpace));
    setMaint(mergeMaintenance(d.maintenance));
    setLoaded(true);
  }, (e) => toast.error('Could not load rules: ' + e.message)), []);

  useEffect(() => onSnapshot(collection(analyticsDb, 'mutes'), (snap) => {
    setMutes(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
  }), []);

  useEffect(() => onValue(ref(db, 'mdm/groups'), (snap) => setGroups(snap.val() || {})), []);

  useEffect(() => onSnapshot(doc(analyticsDb, 'fleet', 'summary'), (snap) => {
    setAutoSummary(snap.exists() ? snap.data().autoFreeSpace || null : null);
  }, () => {}), []);

  const groupName = (key) => (groups[key] && groups[key].name) || decodeURIComponent(key || '');
  const groupOptions = useMemo(
    () => Object.keys(groups).map((k) => ({ label: (groups[k] && groups[k].name) || decodeURIComponent(k), value: k })),
    [groups],
  );

  const current = {
    rules, businessHours: { default: defaultHours, groups: groupHours }, scanEveryMinutes: Number(scanEvery), autoFreeSpace: autoFree, maintenance: maint,
  };
  const savedShape = saved ? {
    rules: mergeRules(saved.rules),
    businessHours: { default: (saved.businessHours && saved.businessHours.default) || DEFAULT_HOURS, groups: (saved.businessHours && saved.businessHours.groups) || {} },
    scanEveryMinutes: Number(saved.scanEveryMinutes || 15),
    autoFreeSpace: mergeAutoFreeSpace(saved.autoFreeSpace),
    maintenance: mergeMaintenance(saved.maintenance),
  } : null;
  const dirty = loaded && JSON.stringify(current) !== JSON.stringify(savedShape);

  const save = async () => {
    setSaving(true);
    try {
      await setDoc(doc(analyticsDb, 'config', 'rules'), {
        ...current,
        version: increment(1),
        updatedAt: serverTimestamp(),
        updatedBy: getAuth().currentUser?.email || '',
      }, { merge: true });
      toast.success('Rules saved. They apply within a minute; the next scan re-checks the whole fleet.');
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const recalculate = async () => {
    setRecalc(true);
    try {
      const res = await httpsCallable(analyticsFunctions, 'mdmAnalyticsRecalculate', { timeout: 300000 })();
      const r = res.data || {};
      toast.success(`Recalculated ${r.devices ?? ''} devices · ${r.problemDevices ?? 0} with problems`);
    } catch (e) {
      toast.error('Recalculate failed: ' + e.message);
    } finally {
      setRecalc(false);
    }
  };

  const addMute = async () => {
    const key = muteForm.kind === 'group' ? muteForm.key : muteForm.key.trim().toUpperCase();
    if (!key) { toast.error(muteForm.kind === 'group' ? 'Choose a group' : 'Enter the device MAC'); return; }
    try {
      await addDoc(collection(analyticsDb, 'mutes'), {
        kind: muteForm.kind,
        key,
        until: muteForm.until ? new Date(`${muteForm.until}T23:59:59`).getTime() : null,
        reason: muteForm.reason.trim(),
        createdAt: serverTimestamp(),
        createdBy: getAuth().currentUser?.email || '',
      });
      setMuteForm({ kind: muteForm.kind, key: '', until: '', reason: '' });
      toast.success('Muted. It leaves the problem list at the next scan.');
    } catch (e) {
      toast.error('Could not mute: ' + e.message);
    }
  };

  const setRule = (code) => (next) => setRules((r) => ({ ...r, [code]: next }));
  const section = (name) => Object.keys(DEFAULT_RULES).filter((c) => RULE_META[c].section === name);

  return (
    <div className="page-shell">
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />

      {/* Rules has no menu item - it is opened from the Fleet health page. */}
      <Link to="/health" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-3">
        <ChevronLeft size={14} /> Fleet health
      </Link>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="brand-icon-badge"><SlidersHorizontal size={18} /></div>
          <div>
            <h1 className="text-xl font-semibold text-white">Problem rules</h1>
            <p className="text-xs text-gray-500">
              {saved?.updatedAt ? `Last saved ${saved.updatedAt.toDate().toLocaleString()}${saved.updatedBy ? ` · ${saved.updatedBy}` : ''}` : 'Using the default rules'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 md:ml-auto">
          <button type="button" onClick={recalculate} disabled={recalc} className="btn-secondary">
            <RotateCw size={16} className={recalc ? 'animate-spin' : ''} /> {recalc ? 'Recalculating…' : 'Recalculate now'}
          </button>
          <button type="button" onClick={save} disabled={!dirty || saving} className="btn-primary">
            <Save size={16} /> {saving ? 'Saving…' : 'Save rules'}
          </button>
        </div>
      </div>

      <section className="card-chrome mb-4">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><Cpu size={18} /></div>
          <div>
            <h2 className="text-sm font-semibold text-white">Device health</h2>
            <p className="text-xs text-gray-500">Checked every time a device reports its data.</p>
          </div>
        </header>
        <div className="space-y-2">
          {section('device').map((code) => <RuleRow key={code} code={code} rule={rules[code]} onChange={setRule(code)} />)}
        </div>
      </section>

      <section className="card-chrome mb-4">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><Activity size={18} /></div>
          <div>
            <h2 className="text-sm font-semibold text-white">Connection and fleet</h2>
            <p className="text-xs text-gray-500">Checked by the fleet scan.</p>
          </div>
          <div className="ml-auto w-44">
            <CustomDropdown label="" value={scanEvery} onChange={setScanEvery} options={SCAN_OPTIONS} />
          </div>
        </header>
        <div className="space-y-2">
          {section('fleet').map((code) => <RuleRow key={code} code={code} rule={rules[code]} onChange={setRule(code)} />)}
        </div>
      </section>

      <section className="card-chrome mb-4">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><Eraser size={18} /></div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-white">Automatic free up space</h2>
            <p className="text-xs text-gray-500">
              Sends the same command as the device's "Free up space" button (app caches + Downloads) when free storage
              drops below the limit. Only online devices, never over another pending command, fullest first.
            </p>
          </div>
          <div className="ml-auto">
            <Toggle on={!!autoFree.enabled} label="Automatic free up space" onClick={() => setAutoFree({ ...autoFree, enabled: !autoFree.enabled })} />
          </div>
        </header>
        <div className={`card-inset p-3 flex flex-wrap gap-4 ${autoFree.enabled ? '' : 'opacity-60'}`}>
          {[
            ['belowPct', 'When free storage is below', '% of the device'],
            ['cooldownHours', 'At most once every', 'hours'],
            ['maxPerScan', 'At most', 'devices per scan'],
            ['minFreedMb', 'Stop if it frees less than', 'MB twice'],
          ].map(([key, label, unit]) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wider text-gray-400">{label}</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min="0"
                  step="any"
                  disabled={!autoFree.enabled}
                  value={autoFree[key] ?? ''}
                  onChange={(e) => setAutoFree({ ...autoFree, [key]: e.target.value === '' ? '' : Number(e.target.value) })}
                  className="input-field w-24 py-1.5"
                />
                <span className="text-xs text-gray-500">{unit}</span>
              </div>
            </label>
          ))}
        </div>
        {autoSummary && (
          <div className="mt-3 text-xs text-gray-500">
            Last scan sent it to {autoSummary.sentThisScan || 0} device{autoSummary.sentThisScan === 1 ? '' : 's'}.
            {(autoSummary.needsManual || []).length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-amber-300">Needs a manual check - clean-up did not help (space is held by apps, not caches):</p>
                {autoSummary.needsManual.map((d) => (
                  <p key={d.mac} className="text-gray-400">
                    {d.name} · {groupName(d.group) || 'No group'} · {d.mac}
                    {d.lastFreedMb !== null && d.lastFreedMb !== undefined ? ` · last freed ${d.lastFreedMb} MB` : ''}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="card-chrome mb-4">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><Wrench size={18} /></div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-white">Automatic maintenance</h2>
            <p className="text-xs text-gray-500">
              When a device comes online - a new one, or one that was offline - it gets the MUSICDJ Player and the
              Updater marked "Set as Latest" if it misses them or runs older ones. One install at a time, never over
              another command, never a downgrade; the first device of a model goes alone; stops after two failures.
            </p>
          </div>
          <div className="ml-auto">
            <Toggle on={!!maint.enabled} label="Automatic maintenance" onClick={() => setMaint({ ...maint, enabled: !maint.enabled })} />
          </div>
        </header>
        <div className={`space-y-2 ${maint.enabled ? '' : 'opacity-60'}`}>
          {[
            ['installPlayer', 'Install MUSICDJ Player', 'rs.musicdj.player - the released version'],
            ['installUpdater', 'Install Updater', 'rs.musicdj.updater on firmware boxes, rs.musicdj.mdmupdater on other devices'],
          ].map(([key, label, help]) => (
            <div key={key} className="card-inset p-3 flex items-center gap-3">
              <Toggle on={!!maint[key]} label={label} onClick={() => setMaint({ ...maint, [key]: !maint[key] })} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">{label}</p>
                <p className="text-xs text-gray-500">{help}</p>
              </div>
            </div>
          ))}
          <div className="card-inset p-3">
            <p className="text-sm font-semibold text-white">Groups that never get the player automatically</p>
            <p className="text-xs text-gray-500 mb-2">For example AQUA LAB - old player, moving to the new platform.</p>
            <div className="flex flex-wrap items-center gap-2">
              {(maint.excludePlayerGroups || []).map((g) => (
                <span key={g} className="brand-pill inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs font-medium">
                  {groupName(g)}
                  <button
                    type="button"
                    aria-label={`Remove ${groupName(g)}`}
                    onClick={() => setMaint({ ...maint, excludePlayerGroups: maint.excludePlayerGroups.filter((x) => x !== g) })}
                    className="rounded-full p-0.5 hover:bg-white/10"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <div className="w-56">
                <CustomDropdown
                  label=""
                  value={excludeToAdd}
                  onChange={(v) => {
                    setMaint({ ...maint, excludePlayerGroups: [...(maint.excludePlayerGroups || []), v] });
                    setExcludeToAdd('');
                  }}
                  placeholder="Add group…"
                  options={groupOptions.filter((o) => !(maint.excludePlayerGroups || []).includes(o.value))}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="card-chrome mb-4">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><Clock size={18} /></div>
          <div>
            <h2 className="text-sm font-semibold text-white">Business hours</h2>
            <p className="text-xs text-gray-500">Watched 24 hours a day by default. Offline time outside a group's own hours does not count as a problem (Belgrade time).</p>
          </div>
        </header>
        <div className="space-y-2">
          <div className="card-inset p-3 flex flex-col md:flex-row md:items-center gap-3">
            <span className="text-sm font-semibold text-white md:w-48">All groups (default)</span>
            <HoursEditor value={defaultHours} onChange={setDefaultHours} />
          </div>
          {Object.entries(groupHours).map(([key, hours]) => (
            <div key={key} className="card-inset p-3 flex flex-col md:flex-row md:items-center gap-3">
              <span className="text-sm text-white md:w-48 truncate">{groupName(key)}</span>
              <HoursEditor value={hours} onChange={(h) => setGroupHours((g) => ({ ...g, [key]: h }))} />
              <button
                type="button"
                onClick={() => setGroupHours((g) => { const n = { ...g }; delete n[key]; return n; })}
                className="md:ml-auto text-gray-500 hover:text-red-300 transition-colors"
                aria-label="Remove group hours"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <div className="sm:w-64">
              <CustomDropdown
                label=""
                value={groupToAdd}
                onChange={setGroupToAdd}
                placeholder="Group with its own hours…"
                options={groupOptions.filter((o) => !groupHours[o.value])}
              />
            </div>
            <button
              type="button"
              disabled={!groupToAdd}
              onClick={() => { setGroupHours((g) => ({ ...g, [groupToAdd]: { ...defaultHours } })); setGroupToAdd(''); }}
              className="btn-secondary"
            >
              <Plus size={16} /> Add group hours
            </button>
          </div>
        </div>
      </section>

      <section className="card-chrome">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><BellOff size={18} /></div>
          <div>
            <h2 className="text-sm font-semibold text-white">Muted devices and groups</h2>
            <p className="text-xs text-gray-500">Known issues, devices in repair, venues closed for the season. Muted devices stay out of the problem list and counts.</p>
          </div>
        </header>
        <div className="space-y-2 mb-4">
          {mutes.length === 0 && <p className="text-sm text-gray-500">Nothing is muted.</p>}
          {mutes.map((m) => {
            const expired = m.until && m.until < Date.now();
            return (
              <div key={m.id} className={`card-inset p-3 flex items-center gap-3 ${expired ? 'opacity-50' : ''}`}>
                <span className="brand-pill px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0">{m.kind === 'group' ? 'Group' : 'Device'}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white truncate">{m.kind === 'group' ? groupName(m.key) : m.key}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {m.until ? `${expired ? 'Ended' : 'Until'} ${new Date(m.until).toLocaleDateString()}` : 'No end date'}
                    {m.reason ? ` · ${m.reason}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => deleteDoc(doc(analyticsDb, 'mutes', m.id)).catch((e) => toast.error(e.message))}
                  className="text-gray-500 hover:text-red-300 transition-colors"
                  aria-label="Remove mute"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_160px_1fr_auto] gap-2 items-end">
          <CustomDropdown
            label=""
            value={muteForm.kind}
            onChange={(v) => setMuteForm({ ...muteForm, kind: v, key: '' })}
            options={[{ label: 'Device', value: 'device' }, { label: 'Group', value: 'group' }]}
          />
          {muteForm.kind === 'group' ? (
            <CustomDropdown label="" value={muteForm.key} onChange={(v) => setMuteForm({ ...muteForm, key: v })} placeholder="Choose group…" options={groupOptions} />
          ) : (
            <input
              value={muteForm.key}
              onChange={(e) => setMuteForm({ ...muteForm, key: e.target.value })}
              placeholder="Device MAC, e.g. 90:0E:B3:A7:0E:9C"
              className="input-field font-mono"
            />
          )}
          <input
            type="date"
            value={muteForm.until}
            onChange={(e) => setMuteForm({ ...muteForm, until: e.target.value })}
            className="input-field"
            title="Muted until (empty = no end date)"
          />
          <input
            value={muteForm.reason}
            onChange={(e) => setMuteForm({ ...muteForm, reason: e.target.value })}
            placeholder="Reason (optional)"
            className="input-field"
          />
          <button type="button" onClick={addMute} className="btn-primary">
            <BellOff size={16} /> Mute
          </button>
        </div>
      </section>
    </div>
  );
}
